/**
 * `<ExclusionGraphFigure />` — animated illustration for the Mutual-Exclusion-Graph layer.
 *
 * @remarks
 * Six rule-chips in a loose ring. Two sparks run every loop: a COMPATIBLE spark crosses a solid edge
 * and both endpoints settle cyan (proceed to the bitmap AND); an EXCLUDED spark travels a dashed
 * edge, strikes the `∅` at its midpoint, dies, and its target chip dims to disabled — the engine
 * short-circuits an impossible combination without computing anything. Chips (rules/data) are cyan;
 * the traveling sparks and the edge flash (evaluation happening) are phosphor and transient.
 *
 * The BASE frame is the resolved/completed state (compatible pair lit, excluded target dimmed, `∅`
 * edge marked) so reduced-motion and off-screen idle both show a legible static graph. Motion is
 * added only under `no-preference` while on-screen, and every keyframe returns to the base frame.
 *
 * @packageDocumentation
 */

import { useRef } from 'react';

import { useInView } from '../useInView.js';

/** A rule chip in the exclusion graph. */
interface GraphNode {
	readonly cx: number;
	readonly cy: number;
	/** Resting (completed-frame) state: the compatible pair is `lit`, the excluded target is `dim`. */
	readonly label: string;
	readonly state: 'dim' | 'lit' | 'neutral';
}

const NODE_WIDTH = 82;
const NODE_HEIGHT = 26;

/** Ring of six chips. Coordinates are pill centers in the 460×300 viewBox. */
const NODES = {
	napa: { cx: 300, cy: 250, label: 'napa', state: 'neutral' },
	price: { cx: 95, cy: 250, label: '≤ $15', state: 'lit' },
	red: { cx: 300, cy: 50, label: 'red', state: 'neutral' },
	reserve: { cx: 30, cy: 150, label: 'reserve', state: 'neutral' },
	sauvignon: { cx: 95, cy: 62, label: 'sauvignon', state: 'lit' },
	sparkling: { cx: 388, cy: 150, label: 'sparkling', state: 'dim' },
} satisfies Record<string, GraphNode>;

/** Plain structural edges (gray hairlines) sketching the graph. */
const EDGES: readonly (readonly [keyof typeof NODES, keyof typeof NODES])[] = [
	['sauvignon', 'red'],
	['red', 'sparkling'],
	['sparkling', 'napa'],
	['napa', 'price'],
	['price', 'reserve'],
	['reserve', 'sauvignon'],
];

/** Midpoint of two nodes. */
function midpoint(a: GraphNode, b: GraphNode): { x: number; y: number } {
	return { x: (a.cx + b.cx) / 2, y: (a.cy + b.cy) / 2 };
}

/**
 * The Mutual-Exclusion-Graph figure.
 */
export const ExclusionGraphFigure = (): React.ReactElement => {
	const rootRef = useRef<HTMLDivElement>(null);
	const isInView = useInView(rootRef, { threshold: 0.35 });

	const compatFrom = NODES.sauvignon;
	const compatTo = NODES.price;
	const exclFrom = NODES.red;
	const exclTo = NODES.sparkling;
	const exclMid = midpoint(exclFrom, exclTo);

	return (
		<div
			ref={rootRef}
			className={`fe-fig fe-fig-excl${isInView ? ' is-playing' : ''}`}
			role="img"
			aria-label="A graph of filter rules. One spark crosses a valid edge and both rules light up; another spark hits a 'no' marker on a forbidden edge and the rule it points to goes dark."
		>
			<svg viewBox="0 0 460 300" xmlns="http://www.w3.org/2000/svg" className="fe-fig__svg">
				{/* ── Structural edges ── */}
				{EDGES.map(([fromId, toId]) => {
					const from = NODES[fromId];
					const to = NODES[toId];
					const isExclusion = (fromId === 'red' && toId === 'sparkling');

					return (
						<line
							key={`${fromId}-${toId}`}
							x1={from.cx}
							y1={from.cy}
							x2={to.cx}
							y2={to.cy}
							className={isExclusion ? 'fe-fig-excl__edge is-exclusion' : 'fe-fig-excl__edge'}
						/>
					);
				})}

				{/* ── Compatible edge (emphasized, solid) ── */}
				<line x1={compatFrom.cx} y1={compatFrom.cy} x2={compatTo.cx} y2={compatTo.cy} className="fe-fig-excl__edge is-compat" />

				{/* ── The ∅ marker at the excluded edge midpoint ── */}
				<g className="fe-fig-excl__void" transform={`translate(${exclMid.x}, ${exclMid.y})`}>
					<circle r={10} className="fe-fig-excl__void-ring" />
					<line x1={-6} y1={6} x2={6} y2={-6} className="fe-fig-excl__void-slash" />
				</g>

				{/* ── Chips ── */}
				{Object.entries(NODES).map(([id, node]) => (
					<g key={id} className={`fe-fig-excl__node is-${node.state}`} data-node={id}>
						<rect
							x={node.cx - NODE_WIDTH / 2}
							y={node.cy - NODE_HEIGHT / 2}
							width={NODE_WIDTH}
							height={NODE_HEIGHT}
							rx={13}
							className="fe-fig-excl__chip"
						/>
						<text x={node.cx} y={node.cy} className="fe-fig-excl__chip-label">
							{node.label}
						</text>
					</g>
				))}

				{/* ── Sparks (phosphor, transient) ── */}
				<circle
					className="fe-fig-excl__spark is-compat"
					cx={compatFrom.cx}
					cy={compatFrom.cy}
					r={3.5}
					style={{ '--dx': `${compatTo.cx - compatFrom.cx}px`, '--dy': `${compatTo.cy - compatFrom.cy}px` } as React.CSSProperties}
				/>
				<circle
					className="fe-fig-excl__spark is-excl"
					cx={exclFrom.cx}
					cy={exclFrom.cy}
					r={3.5}
					style={{ '--dx': `${exclMid.x - exclFrom.cx}px`, '--dy': `${exclMid.y - exclFrom.cy}px` } as React.CSSProperties}
				/>

				{/* ── Caption ── */}
				<text x={230} y={290} className="fe-fig-excl__caption">
					<tspan className="fe-fig-excl__caption-void">∅ precomputed</tspan> — selecting sparkling would yield 0 results
				</text>
			</svg>
		</div>
	);
};
